// cesium-camera.ts

import { Cartesian3, Math as CesiumMath, Color, Cartographic } from 'cesium';
import type { CesiumViewSettings, CameraOptions } from './cesium-types';
// Note: getViewer is imported dynamically to avoid circular imports

// State management for camera operations
let initialFlightStarted = false;
let isViewerReady = false;
let initialCameraSetRef = false;

// Helper function to ensure globe visibility
export function ensureGlobeVisibility(viewer: any) {
  if (!viewer || initialFlightStarted) return;
  viewer.scene.globe.baseColor = Color.TRANSPARENT;
  viewer.scene.backgroundColor = Color.TRANSPARENT;
  initialFlightStarted = true;
}

// Helper function to create camera destination and orientation
export function createCameraOptions(settings: CesiumViewSettings, duration: number = 2.0, onComplete?: () => void): CameraOptions {
  const height = Math.max(100, settings.height || 1000);
  
  return {
    destination: Cartesian3.fromDegrees(settings.center[0], settings.center[1], height),
    orientation: {
      heading: settings.bearing ? CesiumMath.toRadians(settings.bearing) : 0,
      pitch: settings.pitch ? CesiumMath.toRadians(settings.pitch) : -CesiumMath.PI_OVER_TWO,
      roll: 0.0
    },
    duration: duration,
    complete: onComplete
  };
}

// Convert current camera position to CesiumViewSettings
export function getCurrentCameraPosition(viewer: any): CesiumViewSettings | null {
  if (!viewer) return null;
  
  const cartographic = Cartographic.fromCartesian(viewer.camera.position);
  return {
    center: [
      CesiumMath.toDegrees(cartographic.longitude),
      CesiumMath.toDegrees(cartographic.latitude)
    ],
    height: cartographic.height,
    bearing: CesiumMath.toDegrees(viewer.camera.heading),
    pitch: CesiumMath.toDegrees(viewer.camera.pitch),
  };
}

// Main function for shouldFlyTo pattern (matches CesiumMap exactly)
export function flyToPosition(flyToSettings: CesiumViewSettings, cesiumCameraPosition?: CesiumViewSettings, viewer?: any) {
  // Match CesiumMap's exact condition check
  if (!isViewerReady || !initialCameraSetRef || !viewer || !cesiumCameraPosition || !flyToSettings) {
    return;
  }

  // Add 10ms delay before flying (exactly like CesiumMap)
  setTimeout(() => {
    if (!viewer) return;
    
    const cameraOptions = createCameraOptions(flyToSettings, 2.0);
    viewer.camera.flyTo(cameraOptions);
  }, 10);
}

// Unified camera transition function
export function flyToView(viewer: any, settings: CesiumViewSettings, duration: number = 2.0, onComplete?: () => void) {
  if (!viewer) return;

  ensureGlobeVisibility(viewer);

  // Use setTimeout like CesiumMap for consistent behavior
  setTimeout(() => {
    if (!viewer) return;
    
    const cameraOptions = createCameraOptions(settings, duration, onComplete);
    viewer.camera.flyTo(cameraOptions);
  }, 10);
}

// Alias for backward compatibility
export function updateCameraPosition(viewer: any, settings: CesiumViewSettings, duration: number = 2.0, onComplete?: () => void) {
  flyToView(viewer, settings, duration, onComplete);
}

// Set camera position immediately (without animation)
export function setCameraPosition(viewer: any, settings: CesiumViewSettings) {
  if (!viewer) return;

  ensureGlobeVisibility(viewer);

  const cameraOptions = createCameraOptions(settings);
  viewer.camera.setView({
    destination: cameraOptions.destination,
    orientation: cameraOptions.orientation
  });
}

// Setup camera event listeners
export function setupCameraEvents(viewer: any, onCameraMove?: (settings: CesiumViewSettings) => void) {
  if (!onCameraMove || !viewer) return;

  // Add camera move end event listener (like CesiumMap)
  viewer.camera.moveEnd.addEventListener(() => {
    if (!viewer) return;
    const cameraPosition = getCurrentCameraPosition(viewer);
    if (cameraPosition) {
      onCameraMove(cameraPosition);
    }
  });

  // Add change event listener for continuous updates
  viewer.camera.changed.addEventListener(() => {
    // No need to log every change event
  });

  // Add move start event listener for debugging
  viewer.camera.moveStart.addEventListener(() => {
    // console.log('Camera move start event triggered');
  });
}

// Camera state management functions
export function setCameraReady(ready: boolean) {
  isViewerReady = ready;
  initialCameraSetRef = ready;
}

export function resetCameraState() {
  initialFlightStarted = false;
  isViewerReady = false;
  initialCameraSetRef = false;
}

export function getCameraState() {
  return {
    initialFlightStarted,
    isViewerReady,
    initialCameraSetRef
  };
} 